import React, { useState, useRef, useEffect } from "react";
import { type NavigateOptions } from "react-router-dom";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import "./SchoolLetterHeader.css";
import apiClient from "../../stateManagers/services/axiosInterceptor";
import "primeicons/primeicons.css";
import LetterEditor from "./LetterEditor";
import type { CrudProps } from "../../router/routeWrappers";
import UnsavedChangesModal from "../../components/UnsavedChangesModal";
import { useOptionFiles } from "../../stateManagers/hooks/useOptionFiles";
import { useOptions } from "../../stateManagers/hooks/useOptions";

import {
  PAGE_W,
  PAGE_H,
  SNAP,
  ZOOM_STEPS,
  FONT_SIZES,
  SCHOOL,
  makeEl as _makeEl,
  makeImgEl as _makeImgEl,
  buildPrint,
  type CanvasElement,
  type TextElement,
  type ImageElement,
  type ElementPatch,
  type LockedBlock,
  makeImgEl,
  makeEl,
} from "./SchoolLetterHeader";

const TEMPLATE_TYPES = [
  { value: "general",    label: "General" },
  { value: "circular",   label: "Circular" },
  { value: "notice",     label: "Notice" },
  { value: "warning",    label: "Warning" },
  { value: "permission", label: "Permission" },
];

interface SchoolConfig {
  name: string;
  headerImage: string | null;
  footerImage: string | null;
}

export default function SchoolLetterTemplate({ id, onNavigate }: CrudProps) {
  // ✅ Use hooks for header/footer images
  const { optionFiles, loading: filesLoading } = useOptionFiles([
    "letterhead_header_image",
    "letterhead_footer_image",
  ]);

  const { options: userOptions, loading: userOptionsLoading } = useOptions([
    "school_name",
    "letterhead_serial",
    "letterhead_date",
  ]);

  const [elements, setEls] = useState<CanvasElement[]>([]);
  const [selId, setSelId] = useState<string | null>(null);
  const [toast, setToast] = useState("");
  const [printing, setPrinting] = useState(false);
  const [templateTitle, setTemplateTitle] = useState("New Template");
  const [templateType, setTemplateType] = useState("general");
  const [zoom, setZoom] = useState(0.75);

  const [schoolConfig, setSchoolConfig] = useState<SchoolConfig>({ name: "School", headerImage: null, footerImage: null });
  const [lockedBlocks, setLockedBlocks] = useState<LockedBlock[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [isDirty, setIsDirty] = useState(false);

  const [hideHeader, setHideHeader] = useState(false);
  const [hideFooter, setHideFooter] = useState(false);

  const [savedSnapshot, setSavedSnapshot] = useState<string>("");
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const isDirtyRef = useRef(false);
  const pendingNavRef = useRef<string | number | null>(null);
  const elementsRef = useRef<CanvasElement[]>(elements);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { elementsRef.current = elements; }, [elements]);

  useEffect(() => {
    const current = JSON.stringify({ title: templateTitle, type: templateType, elements });
    const dirty = current !== savedSnapshot;
    isDirtyRef.current = dirty;
    setIsDirty(dirty);
  }, [elements, templateTitle, templateType, savedSnapshot]);

  // ── Parse school config from hooks ──────────────────────────────────────
  useEffect(() => {
    if (userOptionsLoading || filesLoading) return;

    const schoolName = userOptions.school_name?.data as string || "School";
    const headerImage = optionFiles.letterhead_header_image?.files?.[0]?.url || null;
    const footerImage = optionFiles.letterhead_footer_image?.files?.[0]?.url || null;

    setSchoolConfig({ name: schoolName, headerImage, footerImage });
  }, [userOptionsLoading, filesLoading, userOptions, optionFiles]);

  // ── Parse locked blocks from useOptions ───────────────────────────────────
  useEffect(() => {
    if (userOptionsLoading) return;

    const blocks: LockedBlock[] = [];

    const rawSerial = userOptions.letterhead_serial?.data;
    if (rawSerial) {
      let parsed: any = rawSerial;
      if (typeof rawSerial === 'string') {
        try { parsed = JSON.parse(rawSerial); } catch {}
      }
      if (parsed && typeof parsed.x === 'number' && typeof parsed.y === 'number') {
        blocks.push({ label: "S. No. :-", x: parsed.x, y: parsed.y, width: parsed.width || 440 });
      }
    } else {
      blocks.push({ label: "S. No. :-", x: 80, y: 170, width: 440 });
    }

    const rawDate = userOptions.letterhead_date?.data;
    if (rawDate) {
      let parsed: any = rawDate;
      if (typeof rawDate === 'string') {
        try { parsed = JSON.parse(rawDate); } catch {}
      }
      if (parsed && typeof parsed.x === 'number' && typeof parsed.y === 'number') {
        blocks.push({ label: "Date :-", x: parsed.x, y: parsed.y, width: parsed.width || 200 });
      }
    } else {
      blocks.push({ label: "Date :-", x: 500, y: 170, width: 200 });
    }

    setLockedBlocks(blocks);

    if (id) {
      loadTemplateForEdit(String(id), blocks);
    } else {
      setEls([]);
      setSelId(null);
      setTemplateTitle("New Template");
      setTemplateType("general");
      setSavedSnapshot(JSON.stringify({ title: "New Template", type: "general", elements: [] }));
      setIsDirty(false);
      setOptionsLoading(false);
    }
  }, [userOptionsLoading, userOptions, id]);

  const loadTemplateForEdit = async (templateId: string, fallbackBlocks: LockedBlock[]) => {
    try {
      // ✅ GET /api/letterhead-templates?template_id=xxx
      const res = await apiClient.get(`/api/letterhead-templates?template_id=${templateId}`);
      if (res.data.status === "success" && res.data.template) {
        const t = res.data.template;
        const title = t.title || "Edit Template";
        const type = t.template_type || "general";

        const processedElements = (t.elements || []).map((el: any) => {
          if (el.type === "text" && !el.html) return { ...el, html: el.content || "" };
          return el;
        });

        setTemplateTitle(title);
        setTemplateType(type);
        setEls(processedElements);
        setSavedSnapshot(JSON.stringify({ title, type, elements: processedElements }));

        if (t.locked_blocks && t.locked_blocks.length > 0) {
          setLockedBlocks(t.locked_blocks.map((b: LockedBlock) => {
            const isSerial = b.label.toLowerCase().includes("s. no") || b.label.toLowerCase().includes("serial") || b.label.toLowerCase().includes("no.");
            const isDate = b.label.toLowerCase().includes("date");
            if (isSerial) return { ...b, label: "S. No. :-", width: b.width ?? 440 };
            if (isDate) return { ...b, label: "Date :-" };
            return b;
          }));
        } else {
          setLockedBlocks(fallbackBlocks);
        }
        setOptionsLoading(false);
      }
    } catch (error) {
      console.error('❌ Load template error:', error);
      setOptionsLoading(false);
    }
  };

  const updateLockedBlock = (index: number, x: number, y: number, width?: number) => {
    setLockedBlocks(prev =>
      prev.map((b, i) => i === index ? { ...b, x, y, ...(width !== undefined ? { width } : {}) } : b)
    );
  };

  const safeNavigate = (to: string | number, opts?: NavigateOptions) => {
    if (isDirtyRef.current) {
      pendingNavRef.current = to;
      setShowUnsavedModal(true);
    } else {
      onNavigate(to as any, opts);
    }
  };

  useEffect(() => {
    window.history.pushState({ _templateSentinel: true }, "");
    const onPopState = () => {
      if (!isDirtyRef.current) return;
      window.history.pushState({ _templateSentinel: true }, "");
      pendingNavRef.current = -1;
      setShowUnsavedModal(true);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const showToast = (m: string) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(m);
    toastTimerRef.current = setTimeout(() => setToast(""), 2400);
  };

  const addEl = (o: Partial<TextElement> = {}) => {
    const el = makeEl(o);
    setEls((p) => [...p, el]);
    setSelId(el.id);
  };

  const addImgEl = (o: Partial<ImageElement> = {}) => {
    const el = makeImgEl(o);
    setEls((p) => [...p, el]);
    setSelId(el.id);
  };

  const updateEl = (elId: string, patch: ElementPatch) => {
    setEls((p) => p.map((e) => (e.id === elId ? ({ ...e, ...patch } as CanvasElement) : e)));
  };

  const deleteEl = (elId: string) => {
    setEls((p) => p.filter((e) => e.id !== elId));
    if (selId === elId) setSelId(null);
  };

  const duplicateEl = (elId: string) => {
    const s = elementsRef.current.find((e) => e.id === elId);
    if (!s) return;
    if (s.type === "image") {
      const el = makeImgEl({ ...s, id: undefined as any, x: s.x + 14, y: s.y + 14 });
      setEls((p) => [...p, el]);
      setSelId(el.id);
    } else {
      addEl({ ...(s as TextElement), id: undefined as any, x: s.x + 14, y: s.y + 14 });
    }
  };

  async function generatePdfFromHtml(html: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const element = document.createElement("div");
      element.innerHTML = html;
      document.body.appendChild(element);
      html2canvas(element, { scale: 2, logging: false, useCORS: true })
        .then((canvas) => {
          const pdf = new jsPDF("p", "mm", "a4");
          pdf.addImage(canvas.toDataURL("image/jpeg", 0.98), "JPEG", 0, 0, 210, 297);
          const blob = pdf.output("blob");
          document.body.removeChild(element);
          resolve(blob);
        })
        .catch((err) => { document.body.removeChild(element); reject(err); });
    });
  }

  const performSave = async (): Promise<boolean> => {
    if (!templateTitle.trim()) { showToast("Please enter a title for this template"); return false; }
    if (elements.length === 0) { showToast("Add at least one element before saving"); return false; }

    setPrinting(true);
    try {
      const printHTML = buildPrint(
        elements,
        lockedBlocks,
        schoolConfig.headerImage,
        schoolConfig.footerImage,
        "",
        hideHeader,
        hideFooter,
      );
      await generatePdfFromHtml(printHTML);

      // ✅ POST /api/letterhead-templates or PUT /api/letterhead-templates/:id
      const payload = {
        title: templateTitle.trim(),
        template_type: templateType,
        elements: elements,
        locked_blocks: lockedBlocks,
      };

      console.log('📤 Saving template payload:', payload);

      const response = id
        ? await apiClient.put(`/api/letterhead-templates/${id}`, payload)
        : await apiClient.post("/api/letterhead-templates", payload);

      console.log('📥 Template save response:', response.data);

      if (response.data?.status === "success") {
        showToast(id ? "Template updated successfully" : "Template saved successfully");
        setSavedSnapshot(JSON.stringify({ title: templateTitle.trim(), type: templateType, elements }));

        if (!id) {
          const newId = response.data?.id;
          if (newId) onNavigate(`/letterhead-templates/edit/${newId}`, { replace: true });
        }
        return true;
      }

      showToast("Save failed");
      return false;
    } catch (error: any) {
      console.error('❌ Save error:', error);
      showToast(error.response?.data?.message || "Failed to save. Please try again.");
      return false;
    } finally {
      setPrinting(false);
    }
  };

  const handleSaveOnly = async () => { await performSave(); };
  const handleSaveAndPrint = handleSaveOnly;
  const handlePrintOnly = () => {};
  const handleNewTemplate = () => safeNavigate("/letterhead-templates/new");

  const handleModalKeepEditing = () => { pendingNavRef.current = null; setShowUnsavedModal(false); };

  const handleModalDiscard = () => {
    setSavedSnapshot(JSON.stringify({ title: templateTitle, type: templateType, elements }));
    isDirtyRef.current = false;
    setIsDirty(false);
    const to = pendingNavRef.current;
    pendingNavRef.current = null;
    setShowUnsavedModal(false);
    if (to !== null) onNavigate(to as any);
  };

  const handleModalSave = async () => {
    const saved = await performSave();
    if (saved) {
      const to = pendingNavRef.current;
      pendingNavRef.current = null;
      setShowUnsavedModal(false);
      if (to !== null) onNavigate(to as any);
    }
  };

  if (optionsLoading || userOptionsLoading || filesLoading) {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center",
        justifyContent: "center", flexDirection: "column", gap: 12,
        background: "#f8faff",
      }}>
        <i className="pi pi-spin pi-spinner" style={{ fontSize: 32, color: "#2855ae" }} />
        <p style={{ color: "#6b7280", fontSize: 14, margin: 0 }}>Loading school configuration…</p>
      </div>
    );
  }

  return (
    <>
      {/* ── Type selector strip above editor ── */}
      <div style={{
        background: "white",
        borderBottom: "1px solid #e5e7eb",
        padding: "6px 14px",
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontFamily: "'DM Sans',sans-serif",
        fontSize: 12,
      }}>
        <span style={{ color: "#6b7280", fontWeight: 600, whiteSpace: "nowrap" }}>
          Template Type:
        </span>
        {TEMPLATE_TYPES.map((t) => (
          <button
            key={t.value}
            onClick={() => setTemplateType(t.value)}
            style={{
              padding: "4px 12px",
              borderRadius: 20,
              border: "1px solid",
              borderColor: templateType === t.value ? "#1e3a8a" : "#e5e7eb",
              background: templateType === t.value
                ? "linear-gradient(135deg,#1e3a8a,#3b82f6)"
                : "transparent",
              color: templateType === t.value ? "white" : "#374151",
              fontFamily: "'DM Sans',sans-serif",
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <LetterEditor
        elements={elements}
        selId={selId}
        zoom={zoom}
        letterheadTitle={templateTitle}
        printing={printing}
        onTitleChange={setTemplateTitle}
        onAddEl={addEl}
        onAddImgEl={addImgEl}
        onUpdateEl={updateEl}
        onDeleteEl={deleteEl}
        onDuplicateEl={duplicateEl}
        onSaveAndPrint={handleSaveAndPrint}
        onSetSelId={setSelId}
        SCHOOL={{
          ...SCHOOL,
          name: schoolConfig.name,
          headerImage: schoolConfig.headerImage ?? "",
          footerImage: schoolConfig.footerImage ?? "",
        }}
        PAGE_W={PAGE_W}
        PAGE_H={PAGE_H}
        SNAP={SNAP}
        FONT_SIZES={FONT_SIZES}
        isEditing={!!id}
        onNewLetterhead={handleNewTemplate}
        lockedBlocks={lockedBlocks}
        onUpdateLockedBlock={updateLockedBlock}
        currentLetterheadId=""
        isDirty={isDirty}
        onPrintOnly={handlePrintOnly}
        titleBarLabel="Template Composer"
        isTemplateMode={true}
        onSaveOnly={handleSaveOnly}
        onZoomChange={setZoom}
        hideHeader={hideHeader}
        hideFooter={hideFooter}
        onHideHeaderChange={setHideHeader}
        onHideFooterChange={setHideFooter}
      />

      <UnsavedChangesModal
        open={showUnsavedModal}
        onClose={handleModalKeepEditing}
        onDiscard={handleModalDiscard}
        onSave={handleModalSave}
        saving={printing}
      />

      {toast && (
        <div style={{
          position: "fixed", bottom: 28, left: "50%", transform: "translateX(-50%)",
          background: "#1e3a8a", color: "white", padding: "10px 22px",
          borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 99999,
          boxShadow: "0 4px 18px rgba(30,58,138,0.3)",
        }}>
          {toast}
        </div>
      )}
    </>
  );
}